/**
 * Per-customer pricing-email preview + "Copy for Outlook".
 *
 * Renders the saved review's boilerplate for one customer (the increases table is
 * injected automatically), shows a faithful preview, and copies both rich-HTML
 * and plain-text variants to the clipboard so Outlook keeps the table on paste.
 * Mirrors QuoteHtmlPreviewDialog's clipboard pattern. The app sends nothing — the
 * user pastes into Outlook and sends manually.
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import type { PricingReviewCustomerRow } from '@techgitter/contracts';

import { renderPricingEmail, increasingLines } from '../../utils/pricingEmailRenderer';

interface PricingEmailDialogProps {
  open: boolean;
  onClose: () => void;
  customer: PricingReviewCustomerRow | null;
  subjectTemplate: string;
  bodyTemplate: string;
}

export const PricingEmailDialog: React.FC<PricingEmailDialogProps> = ({
  open,
  onClose,
  customer,
  subjectTemplate,
  bodyTemplate,
}) => {
  const [feedback, setFeedback] = useState<string | null>(null);

  const rendered = useMemo(
    () =>
      customer
        ? renderPricingEmail(subjectTemplate, bodyTemplate, customer)
        : null,
    [customer, subjectTemplate, bodyTemplate],
  );

  const hasIncreases = customer ? increasingLines(customer).length > 0 : false;

  const handleCopy = async () => {
    if (!rendered) return;
    try {
      if (
        typeof window !== 'undefined' &&
        window.ClipboardItem &&
        navigator.clipboard?.write
      ) {
        const item = new window.ClipboardItem({
          'text/html': new Blob([rendered.html], { type: 'text/html' }),
          'text/plain': new Blob([rendered.plain], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(rendered.plain);
      }
      setFeedback('Copied to clipboard — paste into Outlook.');
    } catch (err) {
      setFeedback(`Copy failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleClose = () => {
    setFeedback(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Pricing email — {customer?.customerName || 'customer'}
      </DialogTitle>
      <DialogContent dividers>
        {feedback && (
          <Alert
            severity={feedback.startsWith('Copy failed') ? 'error' : 'success'}
            sx={{ mb: 2 }}
            onClose={() => setFeedback(null)}
          >
            {feedback}
          </Alert>
        )}

        {customer && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>To:</strong>{' '}
              {customer.customerEmail || (
                <em>no email on customer record</em>
              )}
            </Typography>
            {rendered && (
              <Typography variant="body2" color="text.secondary">
                <strong>Subject:</strong> {rendered.subject}
              </Typography>
            )}
          </Box>
        )}

        {!hasIncreases && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This customer has no increasing products for the selected scenario, so
            the increases table is empty.
          </Alert>
        )}

        {rendered && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              p: 2,
              bgcolor: 'background.paper',
            }}
            // Same HTML that gets copied, so the preview is faithful.
            dangerouslySetInnerHTML={{ __html: rendered.html }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button variant="contained" onClick={handleCopy} disabled={!rendered}>
          Copy for Outlook
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PricingEmailDialog;
